import React, { useState } from 'react';

export default function WorkOrderCommentForm({ 
  workOrderId, 
  onSubmit, 
  canEscalate = false,
  isLoading = false 
}) {
  const [comment, setComment] = useState('');
  const [isEscalation, setIsEscalation] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!comment.trim()) {
      return;
    }

    await onSubmit({
      comment: comment.trim(),
      isEscalation,
      photoFile
    });

    // Reset form
    setComment('');
    setIsEscalation(false);
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white border-t border-gray-200 pt-4">
      <div className="mb-3">
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Add a comment..."
          rows={3}
          className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          disabled={isLoading}
        />
      </div>

      {/* Photo Preview */}
      {photoPreview && (
        <div className="mb-3 relative inline-block">
          <img 
            src={photoPreview} 
            alt="Preview" 
            className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200"
          />
          <button
            type="button"
            onClick={removePhoto}
            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
          >
            ×
          </button>
        </div>
      )}

      {/* Options and Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-6">
          {/* File Upload */}
          <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-colors group">
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              className="hidden"
              disabled={isLoading}
            />
            <svg className="w-6 h-6 text-gray-600 group-hover:text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">Image Attachment</span>
          </label>

          {/* Urgent Checkbox */}
          {canEscalate && (
            <label className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md hover:bg-orange-50 hover:border-orange-300 transition-colors cursor-pointer">
              <input
                type="checkbox"
                checked={isEscalation}
                onChange={(e) => setIsEscalation(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500 focus:ring-2 cursor-pointer"
                disabled={isLoading}
              />
              <span className={`text-sm font-medium ${isEscalation ? 'text-orange-700' : 'text-gray-700'}`}>
                Mark Comment As Urgent
              </span>
            </label>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={!comment.trim() || isLoading}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            isEscalation
              ? 'bg-orange-600 hover:bg-orange-700 text-white'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          } disabled:bg-gray-300 disabled:cursor-not-allowed`}
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Posting...
            </span>
          ) : (
            isEscalation ? 'Send Urgent Comment' : 'Post Comment'
          )}
        </button>
      </div>
    </form>
  );
}
